import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SafeBankImportModal } from './SafeBankImportModal';
import { api, ApiRequestError } from "../lib/api";
import { Button, Modal, Money, Select, Spinner } from "./ui";
import type { Account } from "../lib/models";
import type {
  ImportAnalysis,
  ImportNewRow,
  CommitImportRow,
} from "@shared/types";

interface EditableRow extends ImportNewRow {
  include: boolean;
  categoryAccountId: string;
}

export function ImportModal(props: {account:Account;currency:string;onClose:()=>void}) {
  const capability=useQuery({queryKey:['safe-bank-import-capability',localStorage.getItem('gl.orgId')],queryFn:()=>api.get<{enabled:boolean}>('/safe-bank-imports/capability'),retry:false});
  if(capability.isPending) return <Modal open onClose={props.onClose} title="Import transactions"><Spinner label="Checking import options…"/></Modal>;
  if(capability.error && !(capability.error instanceof ApiRequestError && capability.error.status===404)) return <Modal open onClose={props.onClose} title="Import transactions"><p>Import options could not be verified. Close this window and try again.</p></Modal>;
  if(capability.data?.enabled) return <SafeBankImportModal {...props}/>;
  return <LegacyImportModal {...props}/>;
}

function LegacyImportModal({
  account,
  currency,
  onClose,
}: {
  account: Account;
  currency: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [analysis, setAnalysis] = useState<ImportAnalysis | null>(null);
  const [rows, setRows] = useState<EditableRow[]>([]);

  const analyze = useMutation({
    mutationFn: async (file: File) => {
      const csv = await file.text();
      return api.post<ImportAnalysis>(
        `/accounts/${account.id}/import/analyze`,
        { csv },
      );
    },
    onSuccess: (data) => {
      setAnalysis(data);
      setRows(
        data.newRows.map((r) => ({
          ...r,
          include: !!r.suggestedAccountId,
          categoryAccountId: r.suggestedAccountId ?? "",
        })),
      );
    },
  });

  const commit = useMutation({
    mutationFn: () => {
      const toPost: CommitImportRow[] = rows
        .filter((r) => r.include && r.categoryAccountId)
        .map((r) => ({
          date: r.date,
          description: r.description,
          amount: r.amount,
          categoryAccountId: r.categoryAccountId,
        }));
      return api.post<{ posted: number }>(
        `/accounts/${account.id}/import/commit`,
        { rows: toPost },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounts"] });
      onClose();
    },
  });

  const selectedCount = rows.filter((r) => r.include && r.categoryAccountId).length;

  return (
    <Modal open onClose={onClose} title={`Import transactions — ${account.name}`} wide>
      {!analysis && (
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Upload a CSV exported from your bank. We&apos;ll match rows against
            what&apos;s already recorded and suggest categories for anything new.
          </p>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) analyze.mutate(file);
            }}
            className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border file:border-gray-300 file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-gray-50"
          />
          {analyze.isPending && <Spinner label="Analyzing…" />}
          {analyze.error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {errorText(analyze.error)}
            </div>
          )}
        </div>
      )}

      {analysis && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-gray-600">
              {analysis.matched.length} already recorded (skipped)
            </span>
            <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-blue-700">
              {analysis.newRows.length} new
            </span>
            <span
              className={`rounded-full px-2.5 py-0.5 ${
                analysis.aiUsed
                  ? "bg-brand-100 text-brand-700"
                  : "bg-amber-100 text-amber-700"
              }`}
            >
              {analysis.aiUsed ? "AI categorized" : "Rule-based categories"}
            </span>
          </div>

          {analysis.newRows.length === 0 ? (
            <p className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
              Nothing new to import — every row is already in your books.
            </p>
          ) : (
            <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-gray-50">
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-400">
                    <th className="px-3 py-2 w-10"></th>
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Description</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2">Category</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((r, i) => (
                    <tr key={i} className={r.include ? "" : "opacity-40"}>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={r.include}
                          onChange={(e) =>
                            setRows((prev) =>
                              prev.map((x, j) =>
                                j === i ? { ...x, include: e.target.checked } : x,
                              ),
                            )
                          }
                        />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                        {r.date}
                      </td>
                      <td className="px-3 py-2 text-gray-800">{r.description}</td>
                      <td className="px-3 py-2 text-right">
                        <Money cents={r.amount} currency={currency} />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <Select
                            value={r.categoryAccountId}
                            onChange={(e) =>
                              setRows((prev) =>
                                prev.map((x, j) =>
                                  j === i
                                    ? { ...x, categoryAccountId: e.target.value }
                                    : x,
                                ),
                              )
                            }
                            className="min-w-[12rem]"
                          >
                            <option value="">— choose —</option>
                            <optgroup label="Income">
                              {analysis.categoryOptions
                                .filter((o) => o.type === "income")
                                .map((o) => (
                                  <option key={o.id} value={o.id}>
                                    {o.name}
                                  </option>
                                ))}
                            </optgroup>
                            <optgroup label="Expenses">
                              {analysis.categoryOptions
                                .filter((o) => o.type === "expense")
                                .map((o) => (
                                  <option key={o.id} value={o.id}>
                                    {o.name}
                                  </option>
                                ))}
                            </optgroup>
                            <optgroup label="Transfer / not income (bank, equity, liability)">
                              {analysis.categoryOptions
                                .filter(
                                  (o) =>
                                    o.type === "asset" ||
                                    o.type === "liability" ||
                                    o.type === "equity",
                                )
                                .map((o) => (
                                  <option key={o.id} value={o.id}>
                                    {o.name}
                                  </option>
                                ))}
                            </optgroup>
                          </Select>
                          {r.source === "ai" && r.confidence > 0 && (
                            <span className="text-xs text-gray-400">
                              {Math.round(r.confidence * 100)}%
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {commit.error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {errorText(commit.error)}
            </div>
          )}

          <div className="flex items-center justify-between">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            {analysis.newRows.length > 0 && (
              <Button
                onClick={() => commit.mutate()}
                disabled={selectedCount === 0 || commit.isPending}
              >
                {commit.isPending
                  ? "Posting…"
                  : `Post ${selectedCount} transaction${selectedCount === 1 ? "" : "s"}`}
              </Button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : "Something went wrong.";
}
